/**
 * TeacherDetailPage — `/teachers/:id`
 *
 * Teacher profile: avatar/initials, name, subjects, follower count,
 * follow/unfollow, and recent exams. Data from GET /api/teachers/:id/
 * (retrieve already includes `recent_exams` — see TeacherViewSet.retrieve
 * in backend/exams/social_views.py). Mirrors
 * mobile/lib/features/teachers/presentation/teacher_detail_screen.dart.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getTeacher, followTeacher, unfollowTeacher } from '@/api/social'
import { Avatar } from '@/components/kit'
import { ArrowLeft, FileText, Heart, Users } from 'lucide-react'

export default function TeacherDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [teacher, setTeacher] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  function load(config) {
    setLoading(true)
    getTeacher(id, config)
      .then(({ data }) => setTeacher(data))
      .catch((err) => { if (err.code !== 'ERR_CANCELED') setError(err.response?.status === 404 ? 'not_found' : err.message) })
      .finally(() => { if (!config?.signal?.aborted) setLoading(false) })
  }

  useEffect(() => {
    const controller = new AbortController()
    load({ signal: controller.signal })
    return () => controller.abort()
  }, [id])

  async function toggleFollow() {
    if (!teacher || busy) return
    setBusy(true)
    const wasFollowing = teacher.is_following
    setTeacher((tc) => ({
      ...tc,
      is_following: !wasFollowing,
      followers_count: tc.followers_count + (wasFollowing ? -1 : 1),
    }))
    try {
      if (wasFollowing) await unfollowTeacher(teacher.id)
      else await followTeacher(teacher.id)
    } catch {
      load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <button
        onClick={() => navigate('/teachers')}
        className="btn btn-ghost"
        style={{ padding: '8px 12px', marginBottom: 16 }}
      >
        <ArrowLeft size={16} /> {t('common.back', 'Orqaga')}
      </button>

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }} />
        </div>
      )}

      {!loading && error === 'not_found' && (
        <div className="card" style={{ padding: 32, borderRadius: 22, textAlign: 'center', borderStyle: 'dashed', boxShadow: 'none' }}>
          <Users size={36} strokeWidth={1.5} style={{ margin: '0 auto 10px', color: 'var(--muted)' }} />
          <div className="t-sm t-mut">{t('teachers.notFound', "O'qituvchi topilmadi")}</div>
        </div>
      )}

      {!loading && error && error !== 'not_found' && (
        <div className="t-sm" style={{ color: 'var(--danger)' }}>{error}</div>
      )}

      {!loading && teacher && (
        <>
          {/* Profile card */}
          <div className="card" style={{ padding: 24, borderRadius: 22, marginBottom: 16, textAlign: 'center' }}>
            <div className="flex justify-center">
              <Avatar name={teacher.full_name} size={88} />
            </div>
            <div className="h-display" style={{ fontSize: 20, marginTop: 14 }}>{teacher.full_name}</div>
            <div className="t-xs t-mut" style={{ marginTop: 2 }}>@{teacher.username}</div>

            {teacher.subjects?.length > 0 && (
              <div className="t-sm fw-6" style={{ color: 'var(--butter)', marginTop: 8 }}>
                {teacher.subjects.join(', ')}
              </div>
            )}

            <div className="row gap-24" style={{ justifyContent: 'center', marginTop: 16 }}>
              <div>
                <div className="h-display" style={{ fontSize: 20, color: 'var(--coral)' }}>{teacher.exams_count}</div>
                <div className="t-xs t-mut">{t('teachers.exams', 'imtihon')}</div>
              </div>
              <div>
                <div className="h-display" style={{ fontSize: 20 }}>{teacher.followers_count}</div>
                <div className="t-xs t-mut">{t('teachers.followers', 'kuzatuv')}</div>
              </div>
            </div>

            <button
              onClick={toggleFollow}
              disabled={busy}
              className={'btn ' + (teacher.is_following ? 'btn-soft' : 'btn-primary')}
              style={{ marginTop: 18 }}
            >
              <Heart size={14} fill={teacher.is_following ? 'currentColor' : 'none'} color={teacher.is_following ? undefined : '#fff'} />
              {teacher.is_following ? t('teachers.followingBtn', 'Kuzatilmoqda') : t('teachers.followBtn', 'Kuzatish')}
            </button>
          </div>

          {/* Recent exams */}
          <div className="card" style={{ padding: 8, borderRadius: 22 }}>
            <div style={{ padding: '12px 16px' }}>
              <div className="h-display dot" style={{ fontSize: 18 }}>{t('teachers.recentExams', "So'nggi imtihonlar")}</div>
            </div>
            {(!teacher.recent_exams || teacher.recent_exams.length === 0) && (
              <div className="t-sm t-mut" style={{ padding: '14px 16px' }}>{t('teachers.noExams', "Hali imtihonlar yo'q")}</div>
            )}
            {teacher.recent_exams?.map((ex, i) => (
              <button
                key={ex.id}
                onClick={() => navigate(`/exam/${ex.code}`)}
                className="row gap-16"
                style={{
                  width: '100%', textAlign: 'left', background: 'transparent', border: 0, cursor: 'pointer',
                  padding: '14px 16px', borderTop: i ? '1px solid var(--border)' : 0, font: 'inherit',
                }}
              >
                <span
                  className="w-9 h-9 rounded-full grid place-items-center flex-shrink-0 border"
                  style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)', color: 'var(--coral)' }}
                >
                  <FileText size={16} strokeWidth={2} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fw-7 t-sm truncate">{ex.title}</div>
                  {ex.created_at && (
                    <div className="t-xs t-mut" style={{ marginTop: 2 }}>{new Date(ex.created_at).toLocaleDateString()}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
